import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Layout } from '@/layoutDS/Layout'
import { Navigation } from '@/components/Navigation'
import { NavigationLink } from '@/components/NavigationLink'
import SubmittalsNavGroup from '@/components/Navigation/SubmittalsNavGroup'
import useFeature, { features } from '@/hooks/useFeature'
import { Profile } from '@/components/Profile'
import { useAuth } from '@/hooks/useAuth'
import { getDexConfig } from '@/utils/getDexConfig'
import { getEnvAndDex } from '@/utils/getEnvAndDex'
import IconSubmittals from '@/assets/svgs/submittals-icon.svg?react'
import SvgXMarkOutlined from '@/assets/svgs/general-tool/x-mark-outlined'
import IconBuilding from '@/assets/svgs/icon-building.svg?react'
import SvgMenuHamburgerOutlined from '@/assets/svgs/navigation/menu-hamburger-outlined'
import SvgLogoutOutlined from '@/assets/svgs/general-tool/logout-outlined'

export default function AppLayout() {
  const { consultant, logout } = useAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { dex } = getEnvAndDex()
  const { LogoSrc: logoSrc } = getDexConfig(dex)
  const isSubmittalsPhase2 = useFeature(features.SUBMITTALS_PHASE_2)

  // The drawer draws its own copy of the nav, so it takes the same group as the
  // rail — otherwise the phone menu keeps offering a listing the sidebar no
  // longer has.
  const submittalsLink = (
    <NavigationLink to="/submittals" icon={<IconSubmittals />} text="Submittals" />
  )

  return (
    <>
      <Layout>
        <Layout.SideNav>
          <Navigation />
        </Layout.SideNav>
        <Layout.Header>
          {/* Mobile header bar — logo + hamburger, hidden at tablet-p (900px+) */}
          <div className="flex items-center justify-between px-4 py-3 tablet-p:hidden">
            <img src={logoSrc} alt="Logo" style={{ height: '33px' }} />
            <button
              className="flex items-center justify-center w-10 h-10 bg-transparent border-0 cursor-pointer"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <SvgMenuHamburgerOutlined style={{ width: 24, height: 24 }} />
            </button>
          </div>

          {/* Desktop header — breadcrumbs + profile, hidden below tablet-p */}
          <div className="hidden tablet-p:block">
            <div>
              <div className="flex py-6.5 px-8 bg-white">
                <div className="flex items-center justify-start w-1/2">
                  <div id="bread" />
                </div>
                <div className="flex items-center justify-end w-1/2">
                  <div className="flex items-center gap-3">
                    <Profile label={consultant?.name || consultant?.email} onLogout={logout} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Layout.Header>
        <Layout.Main>
          <Outlet />
        </Layout.Main>
      </Layout>

      {/* Dark backdrop behind the mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 tablet-p:hidden"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile menu panel — always in DOM so CSS transitions work; slides in from left */}
      <div
        className={`fixed top-0 left-0 bottom-0 w-4/5 z-50 flex flex-col tablet-p:hidden transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ backgroundColor: '#ffffff' }}
      >
        {/* Top: profile info + close button */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-grey-90">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-[5px] bg-grey-98 flex items-center justify-center">
              <IconBuilding className="w-[25px] h-[25px]" />
            </div>
            <div className="font-avenir-heavy type-body-1">
              {consultant?.name || consultant?.email}
            </div>
          </div>
          <button
            className="flex items-center justify-center w-10 h-10 bg-transparent border-0 cursor-pointer"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <SvgXMarkOutlined style={{ width: 24, height: 24 }} />
          </button>
        </div>

        {/* Nav links. min-h-0 so the list scrolls within the panel rather than
            pushing the logout footer off the bottom once a group is expanded.

            Closing is keyed to a link rather than to any click in here: the group
            header is a click too, and dismissing the drawer on the tap that was
            meant to reveal what is inside it would make the group unusable. */}
        <div
          className="flex-1 min-h-0 overflow-y-auto"
          onClick={e => {
            if (e.target.closest('a')) setIsMobileMenuOpen(false)
          }}
        >
          {isSubmittalsPhase2 ? <SubmittalsNavGroup fallback={submittalsLink} /> : submittalsLink}
        </div>

        {/* Bottom: divider + logout */}
        <div className="border-t border-grey-90" style={{ backgroundColor: '#f9f9ff' }}>
          <button
            className="font-avenir type-body-2 w-full text-left cursor-pointer border-0 flex items-center gap-3 px-5 py-6"
            style={{ backgroundColor: 'transparent' }}
            onClick={() => {
              setIsMobileMenuOpen(false)
              logout()
            }}
          >
            <SvgLogoutOutlined style={{ width: 18, height: 18 }} />
            Logout
          </button>
        </div>
      </div>
    </>
  )
}
